import { log, error } from '../app/logger.js';
import { services } from '../app/services.js';
import { HTMLControl } from '../controls/html_control.js';
import {
  reportPicShrinkError,
  reportPicUploadError,
} from '../ui/report_error.js';
import { fitImage, toDataUrl } from '../util/image_processing.js';
import { window } from '../window.js';

import '../controls/image_slider.js';
import '../map/pick_loc_control.js';

const kPicDescriptions = [
  'Campsite interior',
  'Campsite exterior',
  'Campsite sign',
  'Fire pit',
  'Fire pit and seating',
  'Front view of campsite',
  'Front view of portage',
  'Kitchen area',
  'Kitchen table',
  'Landing area',
  'Picnic table',
  'Portage sign',
  'Portage trail',
  'Privy',
  'Tent spot',
  'Thunderbox',
  'View from the campsite',
  'View from the water',
  'View from the portage',
];

/**
 * Upload pic widget.
 */
class PicUploadControl extends HTMLControl {
  constructor() {
    super();
    this.whenready = new Promise(resolve => (this.onready = resolve));
  }

  static properties = {
    maximized: {
      attribute: 'boolean',
      set(v) {
        this.imageSlider.maximized = v;
      },
    },
    sizezable: {
      attribute: 'boolean',
      set(v) {
        this.imageSlider.sizezable = v;
      },
    },
    searchBounds: {
      set(v) {
        this.searchElement.bounds = v;
      },
    },
    setPics(pics) {
      this.pics = pics;
      this.imageSlider.set(this.pics.map(data => data.urlPromise));
      this.imageSlider.focus();
      this.onready();
    },
  };

  initialize() {
    super.initialize();

    this.searchElement.map = this.map;
    this.searchElement.placeholder = 'Associate picture with location';
    this.searchElement.onpickmodechange = on => {
      this.onpickmodechange((on && this.searchElement) || null);
    };

    this.descriptionInput.addEventListener(
      'input',
      ev => (this.descriptionElement.textContent = ev.target.value)
    );

    this.imageSlider.onclose = () => {
      log(`[picupload] close`);
      this.close();
    };
    this.imageSlider.onminimize = () => {
      log(`[picupload] minimize`);
      this.onminimize && this.onminimize();
    };
    this.imageSlider.onmaximize = () => {
      log(`[picupload] maximize`);
      this.onmaximize && this.onmaximize();
    };
    this.imageSlider.onchange = (...args) => this.picChanged(...args);

    // Upload pic.
    this.querySelector('.dialog-accept').addEventListener('click', () =>
      this.uploadPic()
    );
  }

  focus() {
    if (this.imageSlider) {
      this.imageSlider.focus();
    }
  }

  setPic(picIndex) {
    return this.whenready.then(() => this.imageSlider.setPic(picIndex));
  }

  refreshNavBar() {
    return this.whenready.then(() => this.imageSlider.refresh());
  }

  close() {
    this.searchElement.pickMode = false;
    if (this.onclose) {
      this.onclose();
    }
  }

  picChanged(picIdx, fromUserInput) {
    let { meta, loc } = this.pics[picIdx];

    // Set info.
    let info = `
      <span class='info-name'>${meta.name}</span>
      <span class='info-description'></span>
    `;
    if (meta.date) {
      info += `<span class='info-date'>${meta.date.toLocaleString()}</span>`;
    }
    if (meta.lat && meta.lng) {
      info += `<span class='info-coords'>(${meta.lat}, ${meta.lng})</span>`;
    } else {
      info += `<span class='info-coords'>No GPS coordinates</span>`;
    }
    if (meta.trackPoint) {
      info += `
        <span class='info-track-point'>
          GPS track point:<br>
          ${meta.trackPoint.date.toLocaleString()}<br>
          (${meta.trackPoint.lat}, ${meta.trackPoint.lng})
        </span>
      `;
    }
    this.infoElement.innerHTML = info;

    // Update state.
    this.updateState(loc);

    // Report onchange only if the change was caused by user.
    if (this.onpicchange && fromUserInput) {
      this.onpicchange(this.pics[picIdx]);
    }
  }

  /**
   * Uploads a picture associated with location.
   */
  async uploadPic() {
    if (!this.searchElement.value) {
      window.ReportError(
        `pic-has-no-associated-loc`,
        `No associated location`,
        `Please pick up a location the picture is taken for.`
      );
      return;
    }

    let picIdx = this.imageSlider.picIndex;
    if (picIdx === undefined || picIdx < 0 || picIdx >= this.pics.length) {
      error(
        `[picupload] image slider selected image at ${picIdx} index is out of range, pics count: ${this.pics.length}`
      );
      return;
    }

    this.uploadElement.setAttribute('disabled', 'true');
    this.descriptionInput.readOnly = true;
    this.searchElement.readOnly = true;

    let { blobPromise, meta } = this.pics[picIdx];

    let url = '';
    try {
      url = await toDataUrl(await fitImage(await blobPromise));
    } catch (e) {
      this.uploadElement.removeAttribute('disabled');
      reportPicShrinkError();
      return;
    }

    let description = this.descriptionInput.value;

    let content = url.split(',')[1];
    let picid = await services.location.uploadPicture(
      content,
      Object.assign(meta, { description })
    );
    if (picid === null) {
      this.uploadElement.removeAttribute('disabled');
      reportPicUploadError();
      return;
    }

    let { selectedData } = this.searchElement;
    if (!selectedData) {
      error(`[picupload] no location to associate picture with`);
      return;
    }

    let loc = await this.map.locs.load(selectedData.id);
    let info = { id: loc.id };
    info.pics = loc.pics || [];
    info.pics.push(picid);
    if (!loc.cover) {
      info.cover = picid;
    }
    await await this.map.locs.changeLoc(info);

    this.pics[picIdx].loc = loc; // store association with loc
    this.updateState(loc);
    this.imageSlider.setNavItemClass(picIdx, 'checked');

    this.uploadElement.removeAttribute('disabled');
    this.descriptionInput.value = '';
    this.searchElement.value = '';
    this.descriptionInput.readOnly = false;
    this.searchElement.readOnly = false;
    this.imageSlider.focus();
  }

  updateState(loc) {
    if (loc) {
      this.associatedWithElement.innerHTML = `Associated with <b>${loc.name}</b>`;
      this.setAttribute('uploaded', 'true');
    } else {
      this.removeAttribute('uploaded');
    }
  }

  static markup = `
<image-slider></image-slider>
<div class='info'></div>
<div class='description-edit-container'>
  <input list='picupload-description-list'
         class='description-input'
         placeholder='Picture description'>
  <datalist id='picupload-description-list'>
    ${kPicDescriptions.map(d => `<option>${d}</option>`).join('')}
  </datalist>
</div>
<div class='search-container dialog-buttons'>
  <pick-loc-control class='search'></pick-loc-control>
  <div class='dialog-button dialog-accept upload-button'>Upload</div>
  <div class='associated-with'></div>
</div>`;

  static selectors = {
    AssociatedWith: '.associated-with',
    Description: '.info-description',
    DescriptionInput: '.description-input',
    ImageSlider: 'image-slider',
    Info: '.info',
    Search: '.search',
    Upload: '.upload-button',
  };

  static elements = {
    associatedWithElement: this.selectors.AssociatedWith,
    descriptionElement: this.selectors.Description,
    descriptionInput: this.selectors.DescriptionInput,
    imageSlider: this.selectors.ImageSlider,
    infoElement: this.selectors.Info,
    searchElement: this.selectors.Search,
    uploadElement: this.selectors.Upload,
  };

  #picsNeedsUpdate = false;
}

window.customElements.define('picupload-control', PicUploadControl);

export { PicUploadControl };
